// src/oura/components/docs/write/SendClientEmailDialog.tsx
//
// The last step before a client email leaves (ourteam-backend#6697): who it
// goes to, from whom, and that it cannot be unsent. Same shell as
// DeleteDocDialog: a portal on <body>, Cancel first, Escape cancels, no
// dismiss while the send is in flight. Cancel sends nothing.
//
// A send whose outcome is unknown (the provider never answered) is refused by
// the server until someone checked the Oura inbox's copy; the dialog then
// offers "Send anyway", which says so to the server (`resendUnknown`).

import { createPortal } from "react-dom";
import { Loader2Icon, MailIcon } from "lucide-react";
import type { DocMessagePerson } from "@service/aeo/clientDocsWrite";
import { Button } from "../../../primitives/Button";
import { formatPeople } from "../../../onboarding/drafts";

export function SendClientEmailDialog({
    open,
    docId,
    to,
    cc,
    subject,
    sending,
    failed,
    unconfirmed,
    onSend,
    onClose,
}: {
    open: boolean;
    docId: string;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    sending: boolean;
    /** The server's sentence when the send was refused; the dialog stays open. */
    failed: string | null;
    /** The last send's outcome is unknown: the button sends over it, on purpose. */
    unconfirmed: boolean;
    onSend: (resendUnknown: boolean) => void;
    onClose: () => void;
}) {
    if (!open) return null;
    const cancel = () => !sending && onClose();
    const titleId = `send-client-email-title-${docId}`;
    const first = to[0];
    const who = first ? `${first.name || first.email}${first.name ? ` (${first.email})` : ""}` : "nobody yet";
    const more = to.length - 1 + cc.length;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-send-client-email-dialog={docId}
            // A portal still bubbles through React to the drawer: keep clicks and keys here.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") cancel();
            }}
        >
            <div className="absolute inset-0 bg-gray-900/50" onClick={cancel} />

            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-start gap-3 px-6 pt-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <MailIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 pt-1.5">
                        <h2 id={titleId} className="text-base font-semibold text-gray-900">
                            Send this email to the client?
                        </h2>
                    </div>
                </div>

                <div className="space-y-3 px-6 pb-1 pt-3">
                    <p className="break-words text-sm leading-relaxed text-gray-900">
                        To <span className="font-medium">{who}</span>
                        {more > 0 ? ` and ${more} more` : ""}.
                    </p>
                    {to.length + cc.length > 1 && <p className="m-0 break-words text-xs text-gray-500">{formatPeople([...to, ...cc])}</p>}
                    <p className="m-0 break-words text-sm text-gray-700">
                        <span className="text-gray-500">Subject: </span>
                        {subject}
                    </p>
                    <p className="text-sm leading-relaxed text-gray-600">
                        It goes from Angel at oura@ourteam.ai, with a copy in the Oura inbox, and cannot be unsent.
                    </p>
                    {unconfirmed && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                            <p className="m-0 text-sm font-medium text-amber-900">An earlier send may have gone.</p>
                            <p className="m-0 mt-1 text-xs leading-relaxed text-amber-800">
                                Check the copy in the Oura inbox first. Send anyway only if the client did not get it.
                            </p>
                        </div>
                    )}
                    {failed && (
                        <p role="alert" className="m-0 text-sm text-rose-600">
                            {failed}
                        </p>
                    )}
                </div>

                <div className="flex gap-2 px-6 pb-5 pt-4">
                    {/* Focus starts on Cancel, never on the button that sends. */}
                    <Button variant="outline" className="flex-1" disabled={sending} onClick={cancel} autoFocus>
                        Cancel
                    </Button>
                    <Button className="flex-1" writes disabled={sending || to.length === 0} onClick={() => onSend(unconfirmed)} data-send-client-email-confirm={docId}>
                        {sending ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Sending…
                            </>
                        ) : unconfirmed ? (
                            "Send anyway"
                        ) : (
                            "Send"
                        )}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
